import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BlobButton from './BlobButton';

const Navigation = ({ onOpenModal }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const navLinks = [
    { name: 'Book Writing', route: '/book-writing' },
    { name: 'Book Publishing', route: '/book-publishing' },
    { name: 'Book Marketing', route: '/book-marketing' },
    { name: 'Audiobook', route: '/audiobook-production' },
    { name: 'Book Editing', route: '/book-editing' },
    { name: 'Book Printing', route: '/book-printing' },
    {
      name: 'Other Services',
      dropdown: [
        { name: 'Book Video Trailer', route: '/book-video-trailer' },
        { name: 'Web Design & SEO', route: '/web-design-seo' },
        { name: 'Illustration Services', route: '/illustration-services' },
        { name: 'Article Services', route: '/article-services' },
        { name: 'Book Design Services', route: '/book-design' },
        { name: 'Blog Writing Services', route: '/blog-writing-services' },
        { name: 'Web Content Writing Service', route: '/web-content-writing' }
      ]
    },
    {
      name: 'Company',
      dropdown: [
        { name: 'About Us', route: '/about-us' },
        { name: 'Blog', route: '/blog' },
        { name: 'News & Updates', route: '/news-updates' },
        { name: 'Contact Us', route: '/contact-us' }
      ]
    }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm opacity-100" style={{ backgroundColor: '#ffffff' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={() => navigate('/')}
          >
            <img 
              src="/assets/img/Brand/logo-dark.png" 
              alt="Rushmore Publishing" 
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-6 flex-1 justify-center">
            {navLinks.map((link, index) => (
              link.dropdown ? (
                <div
                  key={index}
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="flex items-center gap-1 text-gray-700 hover:text-amber-700 font-medium transition-colors text-sm">
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 transition-all duration-200 ${
                    activeDropdown === link.name ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}>
                    {link.dropdown.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => item.route ? navigate(item.route) : scrollToSection(item.id)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  key={index}
                  onClick={() => link.route ? navigate(link.route) : scrollToSection(link.id)}
                  className="text-gray-700 hover:text-amber-700 font-medium transition-colors text-sm"
                >
                  {link.name}
                </button>
              )
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block flex-shrink-0">
            <BlobButton
              onClick={onOpenModal}
              variant="white"
            >
              Start Book Publishing
            </BlobButton>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-gray-700 p-2"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 space-y-2 border-t border-gray-200">
            {navLinks.map((link, index) => (
              link.dropdown ? (
                <div key={index}>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                    className="flex items-center justify-between w-full text-left px-4 py-3 text-gray-700 hover:text-amber-700 hover:bg-gray-50 rounded-lg font-medium transition-all"
                  >
                    <span>{link.name}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${
                      activeDropdown === link.name ? 'rotate-180' : ''
                    }`} />
                  </button>
                  {activeDropdown === link.name && (
                    <div className="pl-4 space-y-1">
                      {link.dropdown.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            if (item.route) {
                              navigate(item.route);
                            } else {
                              scrollToSection(item.id);
                            }
                            setIsOpen(false);
                            setActiveDropdown(null);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:text-amber-700 hover:bg-gray-50 rounded transition-colors"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={index}
                  onClick={() => {
                    if (link.route) {
                      navigate(link.route);
                    } else {
                      scrollToSection(link.id);
                    }
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:text-amber-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  {link.name}
                </button>
              )
            ))}
            <div className="px-4 pt-4">
              <BlobButton
                onClick={() => {
                  setIsModalOpen(true);
                  setIsOpen(false);
                }}
                variant="white"
                className="w-full"
              >
                Start Book Publishing
              </BlobButton>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
